import type { FC, PropsWithChildren } from 'react'
import styled from '@emotion/styled'

import ButtonText from '@segment/matcha/components/ButtonText'
import { ChevronRightIcon } from '@segment/matcha/components/Icons/Common'
import Button from '@segment/matcha/components/Button'

import * as CopyText from '@segment/matcha/recipes/CopyText'
import HeroBase, * as HeroRecipe from '@segment/matcha/recipes/Hero'

import heroPng2x from './assets/Hero@2x.png'
import heroWebp from './assets/Hero.webp'
import heroWebp2x from './assets/Hero@2x.webp'

const copy = {
  overline: `Channel Partners`,
  heading: `Become a Twilio Segment Channel Partner`,
  description: `Partner with Twilio, ranked #1 Customer Data Platform for worldwide market share IDC 2022, to help your clients drive revenue and personalization by collecting, unifying, and activating customer data in hundreds of leading software tools.`,
}

const joinSegmentButton = {
  cta: `Become a Channel Partner`,
  url: `https://partners.segment.com/English/register_email.aspx/`,
}
const partnerLoginButton = {
  cta: `Already a partner? Log in`,
  url: `https://partners.segment.com/English/`,
}

const Hero: FC<PropsWithChildren<unknown>> = () => (
  <Wrapper>
    <StyledHeroBase variant="2Col">
      <HeroRecipe.Copy>
        <CopyText.Overline tag="p" variant="h5">
          {copy.overline}
        </CopyText.Overline>
        <CopyText.Heading variant="h1" tag="h1">
          {copy.heading}
        </CopyText.Heading>
        <CopyText.Description variant="pExtraLarge" tag="p">
          {copy.description}
        </CopyText.Description>
        <HeroRecipe.Form>
          <Content>
            <StyledFormButton
              color="primary"
              size="large"
              variant="filled"
              href={joinSegmentButton.url}
              tag="a"
              target="_blank"
            >
              {joinSegmentButton.cta}
            </StyledFormButton>
            <CtaLink href={partnerLoginButton.url} tag="a" target="_blank" iconEnd={ChevronRightIcon}>
              {partnerLoginButton.cta}
            </CtaLink>
          </Content>
        </HeroRecipe.Form>
      </HeroRecipe.Copy>
      <HeroRecipe.Contents>
        <Picture>
          <source type="image/webp" srcSet={`${heroWebp}, ${heroWebp2x} 2x`} />
          <HeroRecipe.Image width="450" height="450" src={heroPng2x} alt="Illustration: Partners Channel" />
        </Picture>
      </HeroRecipe.Contents>
    </StyledHeroBase>
  </Wrapper>
)

const Wrapper = styled.header`
  background-color: ${({ theme }) => theme.palette.twilio.green[10]};
`

const StyledHeroBase = styled(HeroBase)`
  ${HeroRecipe.Wrapper} {
    text-align: left;
  }
`

const Content = styled.div`
  display: flex;
  flex-direction: column;
  row-gap: 16px;
  justify-content: space-between;
  ${({ theme }) => theme.breakpoints.up(`lg`)} {
    align-items: start;
  }
`

const StyledFormButton = styled(Button)`
  width: 100%;
  max-height: 56px;
  display: inline-block;
  white-space: nowrap;
  /* Take up all available space when no adjacent element is present **/
  flex-grow: 1;
  text-align: center;
  ${({ theme }) => theme.breakpoints.up(`lg`)} {
    width: auto;
    flex-grow: 0;
  }
`

const Picture = styled.picture`
  ${HeroRecipe.Image} {
    margin: 0 auto;
  }
`

const CtaLink = styled(ButtonText)`
  ${({ theme }) => theme.typography.h5.css}

  align-items: flex-start;
  justify-content: left;
  width: max-content;
`

export default Hero
